import React, { useEffect, useRef } from 'react';

import markWhite from '../../assets/brand/bizvision-mark-white.png';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

const SATELLITES = 7;
const SIGNAL = '18, 224, 195';

/**
 * Signature hero graphic: the brand mark at the centre of a live node network.
 * Animation pauses when the canvas leaves the viewport or the tab is hidden,
 * and renders a single static frame when the visitor prefers reduced motion.
 */
export default function NetworkCanvas() {
  const canvasRef = useRef(null);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    let nodes = [];
    let frame = null;
    let visible = true;
    let running = false;

    const mark = new Image();
    let markReady = false;
    mark.onload = () => {
      markReady = true;
      if (!running) draw(0);
    };
    mark.src = markWhite;

    function layout() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const min = Math.min(width, height);
      nodes = Array.from({ length: SATELLITES }, (_, i) => ({
        angle: ((Math.PI * 2) / SATELLITES) * i,
        dist: min * (0.34 + (i % 3) * 0.055),
        radius: i % 2 === 0 ? 4.5 : 3,
        speed: 0.06 + (i % 3) * 0.02,
        phase: (i * Math.PI * 2) / SATELLITES,
        filled: i % 2 === 0,
      }));
    }

    function draw(time) {
      const cx = width / 2;
      const cy = height / 2;
      const coreR = Math.min(width, height) * 0.17;
      const t = reducedMotion ? 0 : time;

      ctx.clearRect(0, 0, width, height);

      // Faint orbit guides.
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.lineWidth = 1;
      [0.34, 0.395, 0.45].forEach((ratio) => {
        ctx.beginPath();
        ctx.arc(cx, cy, Math.min(width, height) * ratio, 0, Math.PI * 2);
        ctx.stroke();
      });

      nodes.forEach((node) => {
        const wobble = reducedMotion ? 0 : Math.sin(t * 0.0004 + node.phase) * 8;
        const angle = node.angle + (reducedMotion ? 0 : t * 0.00005 * node.speed);
        node.x = cx + Math.cos(angle) * (node.dist + wobble);
        node.y = cy + Math.sin(angle) * (node.dist + wobble);
      });

      // Links from the core out to every satellite.
      ctx.lineWidth = 1;
      nodes.forEach((node) => {
        const gradient = ctx.createLinearGradient(cx, cy, node.x, node.y);
        gradient.addColorStop(0, `rgba(${SIGNAL}, 0.55)`);
        gradient.addColorStop(1, `rgba(${SIGNAL}, 0.05)`);
        ctx.strokeStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(node.x, node.y);
        ctx.stroke();
      });

      // Data pulses travelling outward along each link.
      if (!reducedMotion) {
        nodes.forEach((node, i) => {
          const progress = ((t * 0.00016 + i / SATELLITES) % 1);
          const eased = 0.18 + progress * 0.82;
          const px = cx + (node.x - cx) * eased;
          const py = cy + (node.y - cy) * eased;
          const fade = Math.sin(progress * Math.PI);

          ctx.beginPath();
          ctx.arc(px, py, 2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${SIGNAL}, ${0.85 * fade})`;
          ctx.fill();
        });
      }

      // Satellite nodes.
      nodes.forEach((node) => {
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        if (node.filled) {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.88)';
          ctx.fill();
        } else {
          ctx.strokeStyle = `rgba(${SIGNAL}, 0.9)`;
          ctx.lineWidth = 1.8;
          ctx.stroke();
        }
      });

      // Core ring plus the official mark inside it.
      ctx.beginPath();
      ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.lineWidth = 2;
      ctx.stroke();

      if (markReady) {
        const size = coreR * 1.28;
        ctx.globalAlpha = 0.96;
        ctx.drawImage(mark, cx - size / 2, cy - size / 2, size, size);
        ctx.globalAlpha = 1;
      }

      if (!reducedMotion && visible) {
        running = true;
        frame = window.requestAnimationFrame(draw);
      } else {
        running = false;
      }
    }

    function start() {
      if (running || reducedMotion) return;
      frame = window.requestAnimationFrame(draw);
    }

    function stop() {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
      running = false;
    }

    function onResize() {
      layout();
      if (!running) draw(0);
    }

    layout();
    draw(0);
    start();

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) start();
        else stop();
      },
      { threshold: 0 }
    );
    io.observe(canvas);

    const onVisibility = () => {
      visible = !document.hidden;
      if (visible) start();
      else stop();
    };

    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      io.disconnect();
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [reducedMotion]);

  return <canvas ref={canvasRef} className="hero__canvas" aria-hidden="true" />;
}
